import { useEffect, useRef, useState } from 'react';
import { useSiteImages } from '../../../hooks/useSiteImages';
import { buildWebPSrcSet, toWebP } from '../../../lib/imageUtils';

export default function AboutOregonDogRescue() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { getImageUrl } = useSiteImages();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 bg-gradient-to-br from-amber-50 via-white to-teal-50/30">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative w-full overflow-hidden" style={{ minHeight: '320px', aspectRatio: '4/3' }}>
              <picture>
                <source
                  type="image/webp"
                  srcSet={buildWebPSrcSet(getImageUrl('puplandia-about'))}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <img
                  src={toWebP(getImageUrl('puplandia-about'), 800)}
                  alt="Oregon Dog Rescue volunteers caring for rescued dogs in Oregon"
                  title="Oregon Dog Rescue - Oregon No-Kill Dog Rescue"
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={600}
                  className="w-full h-full object-cover object-top"
                />
              </picture>
            </div>

            {/* Content */}
            <div className={`p-6 sm:p-8 lg:p-12 flex flex-col justify-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-5 w-fit">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill"></i>
                </div>
                <span>Our Partner</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                About Oregon Dog Rescue
              </h2>

              <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed">
                <strong className="text-gray-900">Oregon Dog Rescue</strong> is a no-kill shelter serving the greater Portland, Oregon area. A 501(c)(3) non-profit organization started in 2007, they are devoted to placing dogs in the best homes and assuring the long-term success of the adoption.
              </p>

              <p className="text-sm sm:text-base text-gray-600 mb-5 leading-relaxed">
                Oregon Dog Rescue is supported solely by adoption fees and donations from the public as they receive no government funding. Their network of foster families and volunteers ensures every dog receives individualized attention and the care they need to find their forever home.
              </p>

              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { icon: 'ri-shield-check-line', label: 'No-Kill Shelter' },
                  { icon: 'ri-nurse-line', label: 'Full Vet Care' },
                  { icon: 'ri-home-heart-line', label: 'Foster Network' },
                ].map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-50 text-teal-600 rounded-xl mx-auto mb-2">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <p className="text-xs font-medium text-gray-700">{item.label}</p>
                  </div>
                ))}
              </div>

              <a
                href="https://www.oregondogrescue.org"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full font-medium hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-fit text-sm sm:text-base"
              >
                <span>Visit OregonDogRescue.org</span>
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
